/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useBrand, useQuery, useQueryContext } from "@contexts/index";
import { removeDuplicate } from "@utils/helpers/index-client";
import { updateDatalayer } from "@utils/helpers/tracking";
import ClosestDealers from "../ClosestDealers";
import Warning from "@assets/icons/warning.svg";
import Tick from "@assets/icons/tick.svg";

type FindDealerProps = {
  label?: string;
  infoText?: string;
  optional?: boolean;
  value?: string;
  hidden?: boolean;
};

const getApiBrand = (brand: string): string =>
  brand === "kinto" ? "toyota" : brand;

const FindDealer = ({
  label,
  infoText,
  value,
  optional,
  hidden,
}: FindDealerProps) => {
  const searchInput = useRef<HTMLInputElement>(null!);
  const brand = useBrand();
  const query = useQuery();
  const { setQuery } = useQueryContext();
  const resolvedLabel =
    label ??
    `${brand === "lexus" ? "Choose" : "Find"} your preferred ${
      brand === "lexus" ? "centre" : "dealer"
    }`;
  const resolvedInfoText = infoText ?? "Enter your postcode/location";
  const { setValue, watch, formState } = useFormContext();
  const [isSearching, setIsSearching] = useState(false);
  const [isDealerFound, setDealerFound] = useState(true);
  const [dealers, setDealers] = useState<any[]>([]);
  const selectedDealer = watch("dealer");

  // Refs to avoid stale closures
  const queryRef = useRef(query);
  const getDealersListRef = useRef<any>(null);
  const lastCoordsRef = useRef<any>(null);
  const lastTermRef = useRef("");
  const lastGroupUsedRef = useRef<string | null>(null);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  // Whenever the selected dealer changes, fill in its name and fire tracking.
  useEffect(() => {
    const dealerMatch = dealers.find((d) => d.value === selectedDealer);
    if (dealerMatch) {
      const name = query?.groupName || dealerMatch.label;
      setValue("dealer_name", name);
      updateDatalayer("dealer", {
        uuid: dealerMatch.uuid,
        name: dealerMatch.name,
        address: {
          city: dealerMatch.address?.city,
          zip: dealerMatch.address?.zip,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDealer, query?.groupName]);

  const applyDealers = (dealersFound: any[]) => {
    const dealersOptions = dealersFound.map((dealerSearch) => ({
      label: dealerSearch?.name,
      value: dealerSearch?.localDealerID,
      name: dealerSearch?.name,
      uuid: dealerSearch?.uuid,
      address: dealerSearch?.address,
    }));

    if (dealersOptions.length) {
      const dealerList = removeDuplicate(dealersOptions, "value");
      setDealers(dealerList);
      setValue("dealer", dealerList[0].value, { shouldValidate: true });
    } else {
      setDealers([]);
    }

    setDealerFound(dealersOptions.length > 0);
  };

  // Re-trigger search when group becomes available from postMessage
  useEffect(() => {
    const coords = lastCoordsRef.current;
    const term = lastTermRef.current;
    const currentGroup = Array.isArray(query?.group)
      ? query.group[0]
      : query?.group;

    if (currentGroup && coords && lastGroupUsedRef.current !== currentGroup) {
      lastGroupUsedRef.current = currentGroup;

      getDealersListRef.current?.(
        coords,
        (result) => applyDealers(result.dealers),
        term
      );
    }

    // Update the ref when group is removed
    if (!currentGroup) {
      lastGroupUsedRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query?.group]);

  useEffect(() => {
    if (value !== undefined) {
      searchInput.current.value = value;
    }
  }, [value]);

  useEffect(() => {
    window.customMap = new mapsWrapper.DynamicMap(
      document.createElement("div"),
      {
        accessToken:
          brand === "lexus"
            ? process.env.NEXT_PUBLIC_LEXUS_MAPBOX_TOKEN
            : process.env.NEXT_PUBLIC_TOYOTA_MAPBOX_TOKEN,
        countryCode: ["GB", "IM", "JE", "GG"],
        language: "en",
      }
    );

    // add auto complete
    new mapsWrapper.AutoComplete({
      autoCompleteInput: searchInput.current,
      accessToken:
        brand === "lexus"
          ? process.env.NEXT_PUBLIC_LEXUS_MAPBOX_TOKEN
          : process.env.NEXT_PUBLIC_TOYOTA_MAPBOX_TOKEN,
      countryCode: ["GB", "IM", "JE", "GG"],
      types: "place,locality,neighborhood,region,postcode",
      onLocationSelect: (location) => {
        if (location) {
          const lon = location.coordinates[0];
          const lat = location.coordinates[1];
          if (getDealersListRef.current) {
            getDealersListRef.current(
              { lat, lon },
              (result) => applyDealers(result.dealers),
              searchInput.current.value
            );
          }
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchByPostcode = (term) => {
    window.customMap.geocoder
      .search(term, { countryCode: ["GB", "IM", "JE", "GG"] })
      .then((geo) => {
        if (geo.length) {
          const lon = geo[0].coordinates[0];
          const lat = geo[0].coordinates[1];
          getDealersList({ lat, lon }, (result) => applyDealers(result.dealers), term);
        } else setDealerFound(false);
      });
  };

  const getDealersList = async (
    coords: any,
    callback: any,
    term = "",
    filterByGroup: any = null
  ) => {
    setIsSearching(true);
    lastCoordsRef.current = coords;
    lastTermRef.current = term;

    const currentQuery = queryRef.current;
    const shouldFilterByGroup =
      filterByGroup !== null ? filterByGroup : currentQuery?.group;

    // Track which group was used for this search
    lastGroupUsedRef.current = shouldFilterByGroup;

    const apiBrand = getApiBrand(brand);
    const url = shouldFilterByGroup
      ? `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${apiBrand}/gb/en/drive/${coords.lon}/${coords.lat}?count=5000&extraCountries=&isCurrentLocation=false&services=ShowRoom`
      : `https://kong-proxy-intranet.toyota-europe.com/dxp/dealers/api/${apiBrand}/gb/en/drive/${coords.lon}/${coords.lat}?count=10&extraCountries=&isCurrentLocation=false&services=ShowRoom`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      if (shouldFilterByGroup) {
        data.dealers = data.dealers.filter(
          (i) => i.parent === currentQuery.group
        );
        data.total = data.dealers.length;
      }

      data.dealers = data.dealers.filter((i) =>
        i.services.some((j) => j.service === "ShowRoom")
      );

      callback(data);
    } catch (error) {
      // Error fetching dealers
    } finally {
      setIsSearching(false);
    }
  };

  // Keep getDealersListRef in sync
  getDealersListRef.current = getDealersList;

  const handleRemoveGroup = () => {
    const coords = lastCoordsRef.current;
    const term = lastTermRef.current;

    if (coords) {
      getDealersList(coords, (result) => applyDealers(result.dealers), term, false);
    }

    setQuery((prevQuery) => {
      const { group, groupName, ...rest } = prevQuery;
      return rest;
    });
  };

  const isError = "dealer" in formState.errors && dealers.length === 0;

  if (hidden) {
    return null;
  }

  return (
    <>
      <div
        className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 md:gap-4 items-start justify-top mb-4 md:mb-2"
        data-gt-name="componentevent"
      >
        <div className="md:col-span-2 lg:col-span-1">
          <label>{resolvedLabel}</label>{" "}
          {optional && (
            <span className="text-xs lowercase whitespace-nowrap">
              (optional)
            </span>
          )}
          <br />
          <p className="text-xs whitespace-nowrap">{resolvedInfoText}</p>
          <div className="relative">
            <input
              type="text"
              id={`places`}
              ref={searchInput}
              defaultValue={value || ""}
              className={` ${isError && "border-red"} ${
                dealers.length > 0 && "border-green"
              } relative p-4 h-[48px] mb-[6px] pl-[40px] mt-2 pr-[52px] w-[100%] text-ellipsis overflow-hidden`}
              data-gt-action="text-input-interaction"
              data-gt-label={`places`}
            />
            <div className="pointer-events-none absolute inset-y-0 bottom-0 left-[2px] flex items-center pl-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className="w-5 h-5  "
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                />
              </svg>
            </div>
            <div
              className={` z-9 absolute inset-y-0 bottom-[-9px] right-[60px] flex items-center pl-3`}
            ></div>
            <div
              className={` z-9 absolute inset-y-0  top-2 right-[40px] flex items-center pl-3`}
            >
              {isError && (
                <Warning className="absolute inset-y-0 right-4 top-4 h-4 pointer-events-none text-red" />
              )}
              {dealers.length > 0 && (
                <Tick className="absolute inset-y-0 right-4 top-4 h-4 pointer-events-none text-green" />
              )}
            </div>

            <button
              disabled={isSearching}
              onClick={(e) => {
                e.preventDefault();
                searchInput.current.value !== "" &&
                  searchByPostcode(searchInput.current.value);
                searchInput.current.value === "" && searchInput.current.focus();
              }}
              className={`absolute right-1 bottom-1  ${
                brand === "lexus"
                  ? " h-12 text-[11px] !uppercase mb-[3px]"
                  : " h-10"
              } px-4 py-1 mb-[6px] p-4  text-sm text-gray capitalize flex items-center justify-center `}
              data-gt-action="find-dealer-cta-click"
              data-gt-label="find"
            >
              {isSearching && <div className="loader dark">Loading...</div>}
              Find
            </button>
          </div>
          {!isDealerFound && (
            <div className="text-red text-tiny flex justify-between pt-1">
              {" "}
              <span>No location found.</span>
            </div>
          )}
          {isError && (
            <div className="text-red text-tiny flex justify-between ">
              {" "}
              <span>
                Please search for your nearest{" "}
                {brand === "lexus" ? `Lexus centre` : `Toyota dealer`}.
              </span>
            </div>
          )}
        </div>
      </div>
      {dealers.length > 0 && (
        <ClosestDealers
          label={
            brand === "lexus"
              ? "Please select a Lexus Centre"
              : "Nominated Dealers"
          }
          helper={
            brand === "lexus"
              ? "List displays the closest centre to your postcode."
              : "List displays the closest dealer to your postcode."
          }
          dealers={dealers}
          showBadge
          onRemoveGroup={handleRemoveGroup}
        />
      )}
    </>
  );
};

FindDealer.displayName = "FindDealer";

export default FindDealer;
